import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince, relativeTime } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate, slugify } from './mock';
import { pushActivity } from './notifications';

export type ArtistOrderKey = 'newest' | 'name' | 'votes';

export interface Artist {
  id: string;
  slug: string;
  name: string;
  discipline: string;
  bio: string;
  email: string;
  avatarMediaId?: string | null;
  avatarUrl?: string | null;
  projects: number;
  proposals: number;
  votes: number;
  updated: string;
  updatedHours: number;
  archived: boolean;
}

/** API shape from `GET /admin/artists` (the mapper derives `archived`). */
export type ArtistDto = Omit<Artist, 'archived'> & {
  archivedAt?: string | null;
  updatedAt?: string;
};

export function mapArtist(dto: ArtistDto): Artist {
  const { archivedAt, updatedAt, ...rest } = dto;
  return {
    ...rest,
    updated: updatedAt ? relativeTime(updatedAt) : rest.updated,
    updatedHours: updatedAt ? hoursSince(updatedAt) : rest.updatedHours,
    archived: Boolean(archivedAt),
  };
}

export interface ArtistInput {
  name: string;
  discipline: string;
  bio: string;
  email: string;
  avatarMediaId: string | null;
  avatarUrl: string | null;
}

export interface ListArtistsParams {
  search?: string;
  order?: ArtistOrderKey;
  status?: 'all' | 'active' | 'archived';
  page?: number;
  pageSize?: number;
}

export type PaginatedArtists = Paginated<Artist>;

export const ARTIST_PAGE_SIZE = 8;
export const ARTIST_PAGE_SIZE_OPTIONS = [8, 16, 24, 48];

let db: ArtistDto[] = [
  {
    id: 'mara-oliveira',
    slug: 'mara-oliveira',
    name: 'Mara Oliveira',
    discipline: 'Painting',
    bio: 'Painter working with water, light and slow observation.',
    email: 'mara@example.com',
    projects: 3,
    proposals: 5,
    votes: 1832,
    updated: '2h ago',
    updatedHours: 2,
  },
  {
    id: 'tomas-beck',
    slug: 'tomas-beck',
    name: 'Tomas Beck',
    discipline: 'Sculpture',
    bio: 'Sculptor carving stone and cast forms.',
    email: 'tomas@example.com',
    projects: 2,
    proposals: 4,
    votes: 1644,
    updated: '6h ago',
    updatedHours: 6,
  },
  {
    id: 'priya-nair',
    slug: 'priya-nair',
    name: 'Priya Nair',
    discipline: 'Mixed media',
    bio: 'Mixed-media artist folding paper, fabric and light.',
    email: 'priya@example.com',
    projects: 2,
    proposals: 3,
    votes: 1120,
    updated: '1d ago',
    updatedHours: 24,
  },
  {
    id: 'jonas-feld',
    slug: 'jonas-feld',
    name: 'Jonas Feld',
    discipline: 'Photography',
    bio: 'Photographer documenting process and decay.',
    email: 'jonas@example.com',
    projects: 1,
    proposals: 2,
    votes: 902,
    updated: '2d ago',
    updatedHours: 48,
  },
  {
    id: 'lena-costa',
    slug: 'lena-costa',
    name: 'Lena Costa',
    discipline: 'Ceramics',
    bio: 'Ceramicist glazing ash and ember tones.',
    email: 'lena@example.com',
    projects: 3,
    proposals: 6,
    votes: 877,
    updated: '3d ago',
    updatedHours: 72,
  },
  {
    id: 'ade-bakare',
    slug: 'ade-bakare',
    name: 'Ade Bakare',
    discipline: 'Printmaking',
    bio: 'Printmaker working with salt, rust and relief.',
    email: 'ade@example.com',
    projects: 2,
    proposals: 3,
    votes: 654,
    updated: '4d ago',
    updatedHours: 96,
  },
  {
    id: 'sofia-marchetti',
    slug: 'sofia-marchetti',
    name: 'Sofia Marchetti',
    discipline: 'Textiles',
    bio: 'Textile artist weaving cast-concrete structures.',
    email: 'sofia@example.com',
    projects: 1,
    proposals: 2,
    votes: 431,
    updated: '5d ago',
    updatedHours: 120,
  },
  {
    id: 'rui-tanaka',
    slug: 'rui-tanaka',
    name: 'Rui Tanaka',
    discipline: 'Digital',
    bio: 'Digital artist growing flora from signal noise.',
    email: 'rui@example.com',
    projects: 1,
    proposals: 1,
    votes: 288,
    updated: '6d ago',
    updatedHours: 144,
  },
  {
    id: 'elena-voss',
    slug: 'elena-voss',
    name: 'Elena Voss',
    discipline: 'Glass',
    bio: 'Glassblower exploring fragile, cultivated light.',
    email: 'elena@example.com',
    projects: 2,
    proposals: 4,
    votes: 512,
    updated: '1w ago',
    updatedHours: 168,
  },
  {
    id: 'marcus-reed',
    slug: 'marcus-reed',
    name: 'Marcus Reed',
    discipline: 'Woodwork',
    bio: 'Woodworker shaping tidal and eroded forms.',
    email: 'marcus@example.com',
    projects: 1,
    proposals: 3,
    votes: 398,
    updated: '2w ago',
    updatedHours: 336,
  },
  {
    id: 'yuki-sato',
    slug: 'yuki-sato',
    name: 'Yuki Sato',
    discipline: 'Illustration',
    bio: 'Illustrator mapping imagined paper cities.',
    email: 'yuki@example.com',
    projects: 2,
    proposals: 5,
    votes: 366,
    updated: '2w ago',
    updatedHours: 340,
  },
  {
    id: 'nina-alvarez',
    slug: 'nina-alvarez',
    name: 'Nina Alvarez',
    discipline: 'Collage',
    bio: 'Collage artist assembling botanical fragments.',
    email: 'nina@example.com',
    projects: 1,
    proposals: 2,
    votes: 244,
    updated: '3w ago',
    updatedHours: 500,
  },
];

function sortArtists(items: ArtistDto[], order: ArtistOrderKey): ArtistDto[] {
  const sorted = [...items];
  switch (order) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'votes':
      sorted.sort((a, b) => b.votes - a.votes);
      break;
    default:
      sorted.sort((a, b) => a.updatedHours - b.updatedHours);
  }
  return sorted;
}

function filterArtists(term?: string): ArtistDto[] {
  const query = term?.trim().toLowerCase();
  if (!query) return db;
  return db.filter(
    (artist) =>
      artist.name.toLowerCase().includes(query) ||
      artist.slug.toLowerCase().includes(query) ||
      artist.discipline.toLowerCase().includes(query),
  );
}

export async function listArtists(params: ListArtistsParams = {}): Promise<PaginatedArtists> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists.get({
      query: {
        search: params.search,
        order: params.order === 'name' ? 'name' : 'newest',
        status: params.status,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<ArtistDto>>(response);
    return { ...page, items: page.items.map(mapArtist) };
  }

  await delay();
  const { search, order = 'newest', status = 'all', page, pageSize = ARTIST_PAGE_SIZE } = params;
  const filtered = filterArtists(search).filter((artist) =>
    status === 'all'
      ? true
      : status === 'archived'
        ? Boolean(artist.archivedAt)
        : !artist.archivedAt,
  );
  const result = paginate(sortArtists(filtered, order), page, pageSize);
  return { ...result, items: result.items.map(mapArtist) };
}

export function queryArtists(params: ListArtistsParams = {}): Promise<PaginatedArtists> {
  return listArtists(params);
}

export async function getArtist(idOrSlug: string): Promise<Artist | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists({ id: idOrSlug }).get();
    return mapArtist(unwrap<ArtistDto>(response));
  }
  await delay();
  const dto = db.find((artist) => artist.slug === idOrSlug || artist.id === idOrSlug);
  return dto ? mapArtist(dto) : undefined;
}

export async function createArtist(input: ArtistInput): Promise<Artist> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists.post({
      name: input.name,
      discipline: input.discipline,
      bio: input.bio,
      email: input.email,
      avatarMediaId: input.avatarMediaId,
    });
    return mapArtist(unwrap<ArtistDto>(response));
  }
  await delay();
  let slug = slugify(input.name);
  while (db.some((artist) => artist.slug === slug)) slug = `${slug}-1`;

  const artist: ArtistDto = {
    id: crypto.randomUUID(),
    slug,
    name: input.name,
    discipline: input.discipline,
    bio: input.bio,
    email: input.email,
    avatarMediaId: input.avatarMediaId,
    avatarUrl: input.avatarUrl,
    projects: 0,
    proposals: 0,
    votes: 0,
    updated: 'just now',
    updatedHours: 0,
  };
  db = [artist, ...db];
  await pushActivity({
    verb: 'created',
    entity: 'artist',
    targetType: 'artist',
    targetSlug: artist.slug,
    targetLabel: artist.name,
  });
  return mapArtist(artist);
}

export async function updateArtist(id: string, input: ArtistInput): Promise<Artist> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists({ id }).patch({
      name: input.name,
      discipline: input.discipline,
      bio: input.bio,
      email: input.email,
      avatarMediaId: input.avatarMediaId,
    });
    return mapArtist(unwrap<ArtistDto>(response));
  }
  await delay();
  let updated: ArtistDto | undefined;
  db = db.map((artist) => {
    if (artist.id !== id) return artist;
    updated = { ...artist, ...input, updated: 'just now', updatedHours: 0 };
    return updated;
  });
  if (!updated) throw new Error(`Artist ${id} not found`);
  await pushActivity({
    verb: 'updated',
    entity: 'artist',
    targetType: 'artist',
    targetSlug: updated.slug,
    targetLabel: updated.name,
  });
  return mapArtist(updated);
}

export async function archiveArtist(id: string): Promise<Artist> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists({ id }).archive.post();
    return mapArtist(unwrap<ArtistDto>(response));
  }
  return setArchived(id, new Date().toISOString());
}

export async function restoreArtist(id: string): Promise<Artist> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.artists({ id }).restore.post();
    return mapArtist(unwrap<ArtistDto>(response));
  }
  return setArchived(id, null);
}

async function setArchived(id: string, archivedAt: string | null): Promise<Artist> {
  await delay();
  let updated: ArtistDto | undefined;
  db = db.map((artist) => {
    if (artist.id !== id) return artist;
    updated = { ...artist, archivedAt, updated: 'just now', updatedHours: 0 };
    return updated;
  });
  if (!updated) throw new Error(`Artist ${id} not found`);
  await pushActivity({
    verb: archivedAt ? 'archived' : 'restored',
    entity: 'artist',
    targetType: 'artist',
    targetSlug: updated.slug,
    targetLabel: updated.name,
  });
  return mapArtist(updated);
}

export async function deleteArtist(id: string): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.artists({ id }).delete();
    return;
  }
  await delay();
  db = db.filter((artist) => artist.id !== id);
}
